import { useRef, useState, useEffect, useCallback } from 'react';
import * as d3 from 'd3-hierarchy';
import { select } from 'd3-selection';
import { zoom as d3Zoom, zoomIdentity } from 'd3-zoom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Slider } from 'primereact/slider';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import mlmService from '../../services/mlmService';

/**
 * Professional MLM Tree Visualization Component with D3.js
 * Features: Smooth zoom slider, proper panning, large readable nodes
 * Display-only version (no edit functionality)
 */
const MLMTreeVisualization = ({
    treeData,
    onNodeClick,
    onLoadChildren,        // NEW: Callback to load children (lazy loading)
    expandingNodeId,       // NEW: ID of node currently loading children
    expandNodeRef,         // NEW: Ref to expose expandNode function to parent
    loading = false,
    currentUserId,
    selectedUserId = null,  // For auto-navigation and highlighting
    isSearchSelection = false,  // NEW: Flag to indicate if selection is from search (triggers path finder)
    onSearchSelect = null,  // NEW: Callback when user selects from search (receives userId)
    customSearchFunction = null  // NEW: Optional custom search function (for OrgChart context)
}) => {
    const svgRef = useRef(null);
    const containerRef = useRef(null);
    const zoomBehaviorRef = useRef(null);
    const currentTransformRef = useRef(null);
    const [hoveredNode, setHoveredNode] = useState(null);
    const [dimensions, setDimensions] = useState({ width: 1200, height: 800 });
    const [zoomLevel, setZoomLevel] = useState(100); // 10% to 200%
    const [expandedNodes, setExpandedNodes] = useState(new Set());
    const isFirstLoadRef = useRef(true); // Track if it's the first tree load
    const [pathToSelected, setPathToSelected] = useState([]); // NEW: Store path for auto-expand

    // Search Panel State (matching OrgChartTree pattern)
    const [searchPanelOpen, setSearchPanelOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchDebounceTimeout, setSearchDebounceTimeout] = useState(null);

    // Constants for tree layout - Professional sizes
    const NODE_WIDTH = 260;
    const NODE_HEIGHT = 150;
    const HORIZONTAL_SPACING = 320;
    const VERTICAL_SPACING = 200;

    // Initialize expanded nodes - only expand root to show direct children
    const initializeExpandedNodes = (node) => {
        const expanded = new Set();

        // ONLY add root node - this shows root's direct children
        // Direct children themselves are collapsed (not in set) so they show "+" button
        expanded.add(node.id);

        return expanded;
    };

    /**
     * Programmatically expand a node (called by parent after loading children)
     * @param {number} nodeId - Node ID to expand
     */
    const expandNode = useCallback((nodeId) => {
        console.log('MLMTreeVisualization: Programmatically expanding node:', nodeId);
        setExpandedNodes(prev => {
            const newSet = new Set(prev);
            newSet.add(nodeId);
            return newSet;
        });
    }, []); // No dependencies - uses functional setState

    // Expose expandNode function to parent component via ref
    useEffect(() => {
        if (expandNodeRef) {
            expandNodeRef.current = expandNode;
        }
    }, [expandNodeRef, expandNode]);

    useEffect(() => {
        if (containerRef.current) {
            const { width, height } = containerRef.current.getBoundingClientRect();
            setDimensions({ width: width || 1200, height: height || 800 });
        }
    }, []);

    // Initialize expanded nodes ONLY on first load
    // NEVER reset on subsequent tree updates (lazy loading children)
    useEffect(() => {
        if (treeData && isFirstLoadRef.current) {
            setExpandedNodes(initializeExpandedNodes(treeData));
            isFirstLoadRef.current = false; // Mark as initialized - never initialize again
        }
    }, [treeData]);

    // Auto-expand path to selected user (ONLY when selected from search)
    useEffect(() => {
        // IMPORTANT: Only fetch path when user is selected FROM SEARCH
        // Regular node clicks should NOT trigger path finder API
        if (!selectedUserId || !treeData || !isSearchSelection) {
            setPathToSelected([]);
            return;
        }

        const fetchAndExpandPath = async () => {
            try {
                console.log('Fetching path for search-selected user:', selectedUserId);
                const response = await mlmService.getUserPath(selectedUserId);
                if (response.success && response.data.path) {
                    // Extract user IDs from path
                    const pathIds = response.data.path.map(user => user.id);
                    setPathToSelected(pathIds);

                    // Auto-expand all nodes along the path
                    setExpandedNodes(prev => {
                        const newExpanded = new Set(prev);
                        pathIds.forEach(id => newExpanded.add(id));
                        return newExpanded;
                    });
                }
            } catch (error) {
                console.error('Failed to fetch path for auto-navigation:', error);
            }
        };

        fetchAndExpandPath();
    }, [selectedUserId, treeData, isSearchSelection]);

    useEffect(() => {
        if (!treeData || !svgRef.current) return;
        renderTree();
    }, [treeData, dimensions, expandedNodes]);

    useEffect(() => {
        if (!svgRef.current || !treeData) return;
        updateNodeAppearance();
    }, [hoveredNode, expandingNodeId]);

    const updateNodeAppearance = () => {
        const svg = select(svgRef.current);
        const nodes = svg.selectAll('.node-content');
        nodes.html(d => renderNodeHTML(d));
    };

    /**
     * Toggle node expansion with lazy loading support
     * @param {number} nodeId - Node ID to toggle
     * @param {object} nodeData - Node data containing has_children and children array
     */
    const toggleNodeExpand = (nodeId, nodeData) => {
        // Check if children need to be loaded (lazy loading)
        if (nodeData.has_children && (!nodeData.children || nodeData.children.length === 0)) {
            // Children NOT loaded yet → Trigger lazy load
            // DON'T expand yet - parent will expand AFTER data loads
            console.log('Node has children but not loaded, triggering lazy load for:', nodeId);
            if (onLoadChildren) {
                onLoadChildren(nodeId);
            }
        } else {
            // Children already loaded OR no children → Just toggle visibility
            setExpandedNodes(prev => {
                const newSet = new Set(prev);
                if (newSet.has(nodeId)) {
                    newSet.delete(nodeId);
                } else {
                    newSet.add(nodeId);
                }
                return newSet;
            });
        }
    };

    const renderTree = () => {
        const svg = select(svgRef.current);
        svg.selectAll('*').remove();

        svg.attr('width', dimensions.width)
           .attr('height', dimensions.height);

        const g = svg.append('g').attr('class', 'zoom-group');

        // Setup smooth zoom and pan behavior
        const zoomBehavior = d3Zoom()
            .scaleExtent([0.1, 2])
            .filter((event) => {
                return !event.button && event.type !== 'dblclick';
            })
            .on('zoom', (event) => {
                currentTransformRef.current = event.transform;
                setZoomLevel(Math.round(event.transform.k * 100));
                g.attr('transform', event.transform);
            });

        zoomBehaviorRef.current = zoomBehavior;
        svg.call(zoomBehavior);

        // Process tree data - only show children of expanded nodes
        const root = d3.hierarchy(treeData, d => {
            // If this node is expanded, show its children
            // If this node is not expanded (collapsed), hide its children
            if (expandedNodes.has(d.id)) {
                return d.children;
            }
            return null; // Hide children
        });

        const treeLayout = d3.tree()
            .nodeSize([HORIZONTAL_SPACING, VERTICAL_SPACING])
            .separation((a, b) => a.parent === b.parent ? 1 : 1.5);

        treeLayout(root);

        const allNodes = [];
        const allLinks = [];

        let minX = Infinity, maxX = -Infinity;
        root.each(node => {
            if (node.x < minX) minX = node.x;
            if (node.x > maxX) maxX = node.x;
        });

        const xOffset = -minX + NODE_WIDTH / 2 + 100;

        root.each(node => {
            node.displayX = node.x + xOffset;
            node.displayY = node.y + NODE_HEIGHT / 2 + 100;
            allNodes.push(node);
        });

        allLinks.push(...root.links());

        // Restore or set initial transform
        if (currentTransformRef.current) {
            svg.call(zoomBehavior.transform, currentTransformRef.current);
        } else {
            const initialScale = 0.8;
            const initialTransform = zoomIdentity
                .translate(dimensions.width * 0.05, 40)
                .scale(initialScale);
            currentTransformRef.current = initialTransform;
            setZoomLevel(Math.round(initialScale * 100));
            svg.call(zoomBehavior.transform, initialTransform);
        }

        // Draw connecting lines
        g.selectAll('.link')
            .data(allLinks)
            .join('path')
            .attr('class', 'link')
            .attr('d', d => {
                const sourceX = d.source.displayX;
                const sourceY = d.source.displayY + NODE_HEIGHT / 2;
                const targetX = d.target.displayX;
                const targetY = d.target.displayY - NODE_HEIGHT / 2;

                return `
                    M ${sourceX},${sourceY}
                    V ${sourceY + 60}
                    H ${targetX}
                    V ${targetY}
                `;
            })
            .attr('fill', 'none')
            .attr('stroke', '#3b82f6')
            .attr('stroke-width', 2);

        // Draw nodes
        const nodeGroups = g.selectAll('.node')
            .data(allNodes)
            .join('g')
            .attr('class', 'node')
            .attr('transform', d => `translate(${d.displayX - NODE_WIDTH / 2}, ${d.displayY - NODE_HEIGHT / 2})`);

        nodeGroups.append('foreignObject')
            .attr('class', 'node-content')
            .attr('width', NODE_WIDTH)
            .attr('height', NODE_HEIGHT)
            .style('overflow', 'visible')
            .style('pointer-events', 'all')
            .append('xhtml:div')
            .style('pointer-events', 'all')
            .html(d => renderNodeHTML(d));
    };

    const renderNodeHTML = (node) => {
        const userData = node.data;
        const userId = userData.id; // Backend returns 'id' (user_id from users table)
        const isCurrentUser = userId === currentUserId;
        const isSelected = userId === selectedUserId; // NEW: Check if this is the searched/selected user
        const isInPath = pathToSelected.includes(userId); // NEW: Check if in path to selected user

        // LAZY LOADING: Check has_children flag (from backend) OR if children already loaded
        const hasChildren = userData.has_children || (userData.children && userData.children.length > 0);
        const childrenLoaded = userData.children && userData.children.length > 0;
        const childCount = userData.children ? userData.children.length : 0;

        // IMPORTANT: Only show "−" (minus) if node is expanded AND children are actually loaded
        // If children aren't loaded yet (empty array), show "+" even if node is in expandedNodes
        const isExpanded = expandedNodes.has(userId) && childrenLoaded;
        const isHovered = hoveredNode === userId;
        const isActive = userData.is_active !== false;

        const statusBadge = isCurrentUser
            ? '<span class="p-tag p-tag-info" style="font-size: 0.7rem; padding: 0.25rem 0.5rem;">You</span>'
            : isActive
                ? '<span class="p-tag p-tag-success" style="font-size: 0.7rem; padding: 0.25rem 0.5rem;">Active</span>'
                : '<span class="p-tag p-tag-danger" style="font-size: 0.7rem; padding: 0.25rem 0.5rem;">Inactive</span>';

        const avatar = `<div style="width: 42px; height: 42px; border-radius: 50%; background: ${isCurrentUser ? '#3b82f6' : isActive ? '#22c55e' : '#94a3b8'}; color: white; font-size: 1.2rem; font-weight: bold; display: flex; align-items: center; justify-content: center; flex-shrink: 0; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
            ${(userData.name?.charAt(0) || 'U').toUpperCase()}
        </div>`;

        const userName = userData.name || 'Unknown User';
        const userEmail = userData.email ? `<p style="margin: 0; font-size: 0.75rem; color: #6b7280; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${userData.email}</p>` : '';

        // Professional styling with selection and path highlighting
        const borderColor = isSelected
            ? '#3B82F6'  // Primary blue for selected (Diamond template primary)
            : isInPath
                ? '#06B6D4'  // Cyan for path nodes (Diamond template info color)
                : isCurrentUser
                    ? '#3b82f6'
                    : isActive
                        ? '#22c55e'
                        : '#94a3b8';

        const borderWidth = isSelected ? '4px' : isInPath ? '3px' : '2px';

        const bgColor = isSelected
            ? '#EFF6FF'  // Light blue for selected (Diamond template primary-50)
            : isInPath
                ? '#ECFEFF'  // Light cyan for path (Diamond template cyan-50)
                : isCurrentUser
                    ? '#eff6ff'
                    : isActive
                        ? '#f0fdf4'
                        : '#f8fafc';

        // Professional box-shadow with glow effect for selected nodes
        const boxShadow = isSelected
            ? '0 0 0 4px rgba(59, 130, 246, 0.1), 0 8px 24px rgba(59, 130, 246, 0.3), 0 4px 8px rgba(0,0,0,0.1)'  // Premium glow
            : isInPath
                ? '0 0 0 2px rgba(6, 182, 212, 0.1), 0 4px 12px rgba(6, 182, 212, 0.2)'  // Subtle path glow
                : '0 2px 6px rgba(0,0,0,0.12)';

        const childBadge = childCount > 0
            ? `<span class="p-badge p-badge-info" style="font-size: 0.75rem;">${childCount}</span>`
            : '';

        const totalDownline = userData.total_downline || childCount;

        // Add animation class for selected node
        const animationClass = isSelected ? ' mlm-node-selected' : '';

        return `
            <div class="mlm-node${animationClass}"
                 style="width: ${NODE_WIDTH}px;
                        height: ${NODE_HEIGHT}px;
                        background: ${bgColor};
                        border: ${borderWidth} solid ${borderColor};
                        border-radius: 8px;
                        padding: 10px;
                        cursor: pointer;
                        box-shadow: ${boxShadow};
                        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                        pointer-events: all;
                        display: flex;
                        flex-direction: column;
                        ${isSelected ? 'transform: scale(1.02);' : ''}"
                 onmouseenter="window.handleMLMNodeHover(${userId}, true)"
                 onmouseleave="window.handleMLMNodeHover(${userId}, false)"
                 onclick="event.stopPropagation(); window.handleMLMNodeClick(${userId})">

                <!-- Top Row: Status Badge + Toggle Button -->
                <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px;">
                    <div>${statusBadge}</div>
                    ${hasChildren ? `
                        <button
                            onclick="event.stopPropagation(); window.handleMLMNodeToggle(${userId}, ${userData.has_children || false}, ${childCount})"
                            style="
                                width: 28px;
                                height: 28px;
                                border-radius: 5px;
                                border: 2px solid ${borderColor};
                                background: ${borderColor};
                                color: white;
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                cursor: pointer;
                                padding: 0;
                                font-size: 1.1rem;
                                font-weight: bold;
                                line-height: 1;
                                box-shadow: 0 2px 6px rgba(0,0,0,0.2);
                                position: relative;
                                z-index: 999;
                                pointer-events: all;
                            "
                            onmouseover="this.style.transform='scale(1.1)';"
                            onmouseout="this.style.transform='scale(1)';"
                        >
                            ${expandingNodeId === userId ? '<i class="pi pi-spin pi-spinner"></i>' : isExpanded ? '−' : '+'}
                        </button>
                    ` : ''}
                </div>

                <!-- Middle Row: Name Block (LEFT) + Avatar (RIGHT) -->
                <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 8px;">
                    <div style="flex: 1; overflow: hidden; min-width: 0;">
                        <h4 style="margin: 0; font-size: 0.95rem; font-weight: 600; color: #111827;
                                   white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                            ${userName}
                        </h4>
                        ${userEmail}
                    </div>
                    ${avatar}
                </div>

                <!-- Stats Row: Two Boxes Side by Side -->
                <div style="display: flex; gap: 6px; margin-bottom: 6px;">
                    <div style="flex: 1; text-align: center; padding: 6px 4px; background: ${isCurrentUser ? '#dbeafe' : '#e0f2fe'}; border-radius: 5px;">
                        <div style="font-size: 0.65rem; color: #6b7280; margin-bottom: 1px;">Direct</div>
                        <div style="font-size: 1rem; font-weight: bold; color: ${isCurrentUser ? '#3b82f6' : '#0284c7'};">${childCount}</div>
                    </div>
                    <div style="flex: 1; text-align: center; padding: 6px 4px; background: #dcfce7; border-radius: 5px;">
                        <div style="font-size: 0.65rem; color: #6b7280; margin-bottom: 1px;">Total</div>
                        <div style="font-size: 1rem; font-weight: bold; color: #16a34a;">${totalDownline}</div>
                    </div>
                </div>

                <!-- Bottom Row: Level Info + Child Badge -->
                <div style="display: flex; align-items: center; justify-content: space-between; padding-top: 6px; border-top: 1px solid #e5e7eb;">
                    <span style="font-size: 0.7rem; color: #9ca3af; display: flex; align-items: center; gap: 3px;">
                        <i class="pi pi-sitemap" style="font-size: 0.7rem;"></i>
                        Level ${userData.depth || 0}
                    </span>
                    ${childBadge}
                </div>
            </div>
        `;
    };

    // Setup window event handlers
    useEffect(() => {
        window.handleMLMNodeClick = (userId) => {
            console.log('MLMTreeVisualization: Node clicked, userId:', userId, 'Type:', typeof userId);
            if (onNodeClick) {
                // Pass the user_id directly (not the whole node object)
                console.log('MLMTreeVisualization: Calling onNodeClick with userId:', userId);
                onNodeClick(userId);
            } else {
                console.warn('MLMTreeVisualization: onNodeClick is not defined');
            }
        };

        window.handleMLMNodeToggle = (userId, hasChildren, childCount) => {
            console.log('MLMTreeVisualization: Toggle clicked for userId:', userId, 'hasChildren:', hasChildren, 'childCount:', childCount);
            const nodeData = {
                has_children: hasChildren,
                children: childCount > 0 ? new Array(childCount) : []
            };
            toggleNodeExpand(userId, nodeData);
        };

        window.handleMLMNodeHover = (userId, isHovering) => {
            setHoveredNode(isHovering ? userId : null);
        };

        return () => {
            delete window.handleMLMNodeClick;
            delete window.handleMLMNodeToggle;
            delete window.handleMLMNodeHover;
        };
    }, [treeData, onNodeClick, onLoadChildren]);

    const handleZoomChange = (value) => {
        if (!zoomBehaviorRef.current || !svgRef.current) return;

        const svg = select(svgRef.current);
        const currentTransform = currentTransformRef.current || zoomIdentity;

        const newScale = value / 100;
        const centerX = dimensions.width / 2;
        const centerY = dimensions.height / 2;

        // Calculate the point in the SVG that's currently at the center of the viewport
        const pointX = (centerX - currentTransform.x) / currentTransform.k;
        const pointY = (centerY - currentTransform.y) / currentTransform.k;

        // Create new transform that keeps this point at the center while zooming
        const newTransform = zoomIdentity
            .translate(centerX, centerY)
            .scale(newScale)
            .translate(-pointX, -pointY);

        currentTransformRef.current = newTransform;
        setZoomLevel(value);
        svg.transition().duration(300).call(zoomBehaviorRef.current.transform, newTransform);
    };

    const handleZoomIn = () => {
        const newZoom = Math.min(200, zoomLevel + 10);
        handleZoomChange(newZoom);
    };

    const handleZoomOut = () => {
        const newZoom = Math.max(10, zoomLevel - 10);
        handleZoomChange(newZoom);
    };

    const handleFitToScreen = () => {
        if (!zoomBehaviorRef.current || !svgRef.current) return;

        const svg = select(svgRef.current);
        const initialScale = 0.8;
        const initialTransform = zoomIdentity
            .translate(dimensions.width * 0.05, 40)
            .scale(initialScale);

        currentTransformRef.current = initialTransform;
        setZoomLevel(Math.round(initialScale * 100));
        svg.transition()
            .duration(300)
            .call(zoomBehaviorRef.current.transform, initialTransform);
    };

    // Search Functions (matching OrgChartTree pattern)
    const handleSearchQueryChange = useCallback((e) => {
        const query = e.target.value;
        setSearchQuery(query);

        // Clear existing timeout
        if (searchDebounceTimeout) {
            clearTimeout(searchDebounceTimeout);
        }

        // Don't search if query is less than 2 characters
        if (query.length < 2) {
            setSearchResults([]);
            return;
        }

        // Set new timeout for debouncing (300ms delay)
        const timeout = setTimeout(() => {
            performSearch(query);
        }, 300);

        setSearchDebounceTimeout(timeout);
    }, [searchDebounceTimeout]);

    const performSearch = async (query) => {
        if (query.length < 2) {
            setSearchResults([]);
            return;
        }

        setSearchLoading(true);
        try {
            // Use custom search function if provided (for OrgChart context), else default MLM search
            const response = customSearchFunction
                ? await customSearchFunction(query, 10)
                : await mlmService.searchUsers(query, 10);

            if (response.success) {
                setSearchResults(response.data.results || []);
            } else {
                console.error('Search error:', response.message);
                setSearchResults([]);
            }
        } catch (error) {
            console.error('Search error:', error);
            setSearchResults([]);
        } finally {
            setSearchLoading(false);
        }
    };

    const handleSearchResultSelect = (user) => {
        if (onSearchSelect) {
            onSearchSelect(user.id);
        }
        // Close search panel after selection
        setSearchPanelOpen(false);
        setSearchQuery('');
        setSearchResults([]);
    };

    const toggleSearchPanel = () => {
        setSearchPanelOpen(!searchPanelOpen);
        if (!searchPanelOpen) {
            // Reset search when opening
            setSearchQuery('');
            setSearchResults([]);
        }
    };

    // Cleanup search timeout on unmount
    useEffect(() => {
        return () => {
            if (searchDebounceTimeout) {
                clearTimeout(searchDebounceTimeout);
            }
        };
    }, [searchDebounceTimeout]);

    if (loading) {
        return (
            <div className="flex align-items-center justify-content-center" style={{ minHeight: '400px' }}>
                <i className="pi pi-spin pi-spinner text-4xl text-primary"></i>
            </div>
        );
    }

    if (!treeData) {
        return (
            <Card className="text-center p-5">
                <i className="pi pi-users text-6xl text-400 mb-3"></i>
                <h3 className="text-xl font-semibold text-600 mb-2">No MLM Network</h3>
                <p className="text-500 mb-0">
                    You haven't joined or created an MLM network yet.
                </p>
            </Card>
        );
    }

    return (
        <div ref={containerRef} className="mlm-tree-container" style={{ width: '100%', height: '100%', minHeight: '400px', position: 'relative', border: '1px solid #cbd5e1', borderRadius: '8px', overflow: 'hidden', background: '#f8fafc' }}>
            <svg ref={svgRef} style={{ width: '100%', height: '100%', cursor: 'grab', touchAction: 'none' }}></svg>

            {/* Zoom Controls - Responsive: hide slider on mobile */}
            <div className="mlm-zoom-controls surface-card border-round shadow-2 p-2 flex align-items-center gap-2">
                <Button
                    icon="pi pi-minus"
                    className="p-button-sm p-button-text p-button-rounded"
                    onClick={handleZoomOut}
                    tooltip="Zoom Out"
                    tooltipOptions={{ position: 'top' }}
                    style={{ width: '28px', height: '28px', padding: 0 }}
                />

                <div className="mlm-zoom-slider hidden md:flex align-items-center gap-2">
                    <Slider
                        value={zoomLevel}
                        onChange={(e) => handleZoomChange(e.value)}
                        min={10}
                        max={200}
                        step={5}
                        style={{ width: '80px' }}
                    />
                    <span className="text-xs font-medium text-600" style={{ minWidth: '38px', textAlign: 'right' }}>
                        {zoomLevel}%
                    </span>
                </div>

                <Button
                    icon="pi pi-plus"
                    className="p-button-sm p-button-text p-button-rounded"
                    onClick={handleZoomIn}
                    tooltip="Zoom In"
                    tooltipOptions={{ position: 'top' }}
                    style={{ width: '28px', height: '28px', padding: 0 }}
                />

                <div className="hidden md:block" style={{ width: '1px', height: '18px', background: '#e2e8f0', margin: '0 2px' }}></div>

                <Button
                    icon="pi pi-window-maximize"
                    className="p-button-sm p-button-text p-button-rounded"
                    onClick={handleFitToScreen}
                    tooltip="Fit to Screen"
                    tooltipOptions={{ position: 'top' }}
                    style={{ width: '28px', height: '28px', padding: 0 }}
                />
            </div>

            {/* Instructions - Hidden on mobile */}
            <div className="mlm-instructions hidden md:block surface-card border-round shadow-2 p-2 md:p-3 text-sm text-600">
                <div className="mb-1"><i className="pi pi-arrows-alt mr-2"></i><strong>Pan:</strong> Drag canvas</div>
                <div><i className="pi pi-search mr-2"></i><strong>Zoom:</strong> Pinch or slider</div>
            </div>

            {/* Floating Search Panel - Same as OrgChartTree */}
            {onSearchSelect && (
                <div className="mlm-search-wrapper">
                    {!searchPanelOpen ? (
                        // Collapsed: Search Button
                        <Button
                            icon="pi pi-search"
                            className="p-button-rounded p-button-primary mlm-search-btn"
                            onClick={toggleSearchPanel}
                            tooltip="Search Network"
                            tooltipOptions={{ position: 'left' }}
                        />
                    ) : (
                        // Expanded: Search Panel - Responsive
                        <div className="mlm-search-panel surface-card border-round-xl shadow-4 p-3 md:p-4 flex flex-column gap-3 md:gap-4">
                            {/* Header */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <i className="pi pi-search" style={{ fontSize: '1.2rem', color: '#3b82f6' }}></i>
                                    <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: '#1e293b' }}>
                                        Search Network
                                    </h3>
                                </div>
                                <Button
                                    icon="pi pi-times"
                                    className="p-button-rounded p-button-text p-button-sm"
                                    onClick={toggleSearchPanel}
                                    style={{ width: '32px', height: '32px' }}
                                />
                            </div>

                            {/* Search Input */}
                            <div className="p-inputgroup">
                                <span className="p-inputgroup-addon">
                                    <i className="pi pi-search"></i>
                                </span>
                                <InputText
                                    value={searchQuery}
                                    onChange={handleSearchQueryChange}
                                    placeholder="Search by name, email, or mobile..."
                                    className="w-full"
                                    autoFocus
                                />
                                {searchQuery && (
                                    <span
                                        className="p-inputgroup-addon cursor-pointer"
                                        onClick={() => {
                                            setSearchQuery('');
                                            setSearchResults([]);
                                        }}
                                    >
                                        <i className="pi pi-times" style={{ color: '#94a3b8' }}></i>
                                    </span>
                                )}
                            </div>

                            {/* Results Area */}
                            <div style={{
                                flex: 1,
                                overflowY: 'auto',
                                maxHeight: '400px',
                                minHeight: '100px'
                            }}>
                                {searchLoading && (
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '40px 0',
                                        flexDirection: 'column',
                                        gap: '12px'
                                    }}>
                                        <ProgressSpinner style={{ width: '40px', height: '40px' }} />
                                        <span style={{ color: '#64748b', fontSize: '0.9rem' }}>Searching...</span>
                                    </div>
                                )}

                                {!searchLoading && searchQuery.length < 2 && (
                                    <div style={{
                                        textAlign: 'center',
                                        padding: '60px 20px',
                                        color: '#94a3b8'
                                    }}>
                                        <i className="pi pi-users" style={{ fontSize: '3.5rem', marginBottom: '16px', display: 'block', color: '#cbd5e1' }}></i>
                                        <p style={{ margin: '0 0 8px 0', fontSize: '1rem', fontWeight: 600, color: '#64748b' }}>
                                            Start typing to search
                                        </p>
                                        <p style={{ margin: 0, fontSize: '0.85rem', color: '#94a3b8' }}>
                                            Search by name, email, or mobile number
                                        </p>
                                    </div>
                                )}

                                {!searchLoading && searchQuery.length >= 2 && searchResults.length === 0 && (
                                    <div style={{
                                        textAlign: 'center',
                                        padding: '60px 20px',
                                        color: '#94a3b8'
                                    }}>
                                        <i className="pi pi-search" style={{ fontSize: '3.5rem', marginBottom: '16px', display: 'block', color: '#cbd5e1' }}></i>
                                        <p style={{ margin: '0 0 8px 0', fontSize: '1rem', fontWeight: 600, color: '#64748b' }}>
                                            No members found matching "{searchQuery}"
                                        </p>
                                        <p style={{ margin: 0, fontSize: '0.85rem', color: '#94a3b8' }}>
                                            Try searching with a different term
                                        </p>
                                    </div>
                                )}

                                {!searchLoading && searchResults.length > 0 && (
                                    <>
                                        <div style={{
                                            fontSize: '0.8rem',
                                            color: '#64748b',
                                            marginBottom: '12px',
                                            fontWeight: 500
                                        }}>
                                            Found {searchResults.length} result{searchResults.length > 1 ? 's' : ''}
                                        </div>
                                        {searchResults.map((user) => {
                                            const avatarLetter = (user.name?.charAt(0) || 'U').toUpperCase();
                                            const isActive = user.is_active !== false;
                                            const avatarColor = isActive ? '#22C55E' : '#94a3b8';

                                            return (
                                                <div
                                                    key={user.id}
                                                    onClick={() => handleSearchResultSelect(user)}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '16px',
                                                        padding: '16px',
                                                        border: '1px solid #e2e8f0',
                                                        borderRadius: '12px',
                                                        marginBottom: '12px',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s',
                                                        background: 'white'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.background = '#f8fafc';
                                                        e.currentTarget.style.borderColor = '#3b82f6';
                                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                                        e.currentTarget.style.boxShadow = '0 6px 20px rgba(59, 130, 246, 0.15)';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.background = 'white';
                                                        e.currentTarget.style.borderColor = '#e2e8f0';
                                                        e.currentTarget.style.transform = 'translateY(0)';
                                                        e.currentTarget.style.boxShadow = 'none';
                                                    }}
                                                >
                                                    {/* Avatar */}
                                                    <div style={{
                                                        width: '50px',
                                                        height: '50px',
                                                        borderRadius: '50%',
                                                        background: avatarColor,
                                                        color: 'white',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: '1.3rem',
                                                        fontWeight: 'bold',
                                                        flexShrink: 0,
                                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                                    }}>
                                                        {avatarLetter}
                                                    </div>

                                                    {/* Info */}
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                                            <span style={{ fontWeight: 600, color: '#1e293b', fontSize: '1rem' }}>
                                                                {user.name}
                                                            </span>
                                                            <span style={{
                                                                background: isActive ? '#dcfce7' : '#f1f5f9',
                                                                padding: '2px 8px',
                                                                borderRadius: '4px',
                                                                fontSize: '0.7rem',
                                                                color: isActive ? '#16a34a' : '#64748b',
                                                                fontWeight: 600
                                                            }}>
                                                                {isActive ? 'Active' : 'Inactive'}
                                                            </span>
                                                        </div>

                                                        {user.email && (
                                                            <div style={{
                                                                fontSize: '0.8rem',
                                                                color: '#64748b',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '6px',
                                                                marginBottom: '4px'
                                                            }}>
                                                                <i className="pi pi-envelope" style={{ fontSize: '0.7rem' }}></i>
                                                                {user.email}
                                                            </div>
                                                        )}

                                                        <div style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '12px',
                                                            fontSize: '0.75rem',
                                                            color: '#94a3b8'
                                                        }}>
                                                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                                <i className="pi pi-sitemap" style={{ fontSize: '0.7rem' }}></i>
                                                                Level {user.level || 0}
                                                            </span>
                                                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                                <i className="pi pi-users" style={{ fontSize: '0.7rem' }}></i>
                                                                {user.total_downline || 0} downline
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {/* Arrow */}
                                                    <i className="pi pi-arrow-right" style={{ color: '#cbd5e1', fontSize: '0.9rem' }}></i>
                                                </div>
                                            );
                                        })}
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            <style>
                {`
                    .mlm-tree-container {
                        font-family: var(--font-family);
                    }

                    .mlm-node:hover {
                        transform: translateY(-3px);
                        box-shadow: 0 6px 16px rgba(0,0,0,0.18) !important;
                    }

                    /* Professional pulse animation for selected node */
                    @keyframes mlm-pulse {
                        0%, 100% {
                            box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1),
                                        0 8px 24px rgba(59, 130, 246, 0.3),
                                        0 4px 8px rgba(0,0,0,0.1);
                        }
                        50% {
                            box-shadow: 0 0 0 8px rgba(59, 130, 246, 0.15),
                                        0 12px 32px rgba(59, 130, 246, 0.4),
                                        0 6px 12px rgba(0,0,0,0.15);
                        }
                    }

                    /* Selected node with premium animation */
                    .mlm-node-selected {
                        animation: mlm-pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
                    }

                    /* Smooth hover for selected nodes */
                    .mlm-node-selected:hover {
                        transform: translateY(-2px) scale(1.02) !important;
                        animation: none;
                    }

                    svg {
                        user-select: none;
                        -webkit-user-select: none;
                        -moz-user-select: none;
                    }

                    svg:active {
                        cursor: grabbing !important;
                    }

                    .mlm-tree-container * {
                        -webkit-user-drag: none;
                        -webkit-tap-highlight-color: transparent;
                    }

                    /* Slider customization */
                    .p-slider .p-slider-handle {
                        width: 18px;
                        height: 18px;
                        background: #3b82f6;
                        border: 2px solid white;
                    }

                    .p-slider .p-slider-range {
                        background: #3b82f6;
                    }

                    /* Responsive Controls Positioning */
                    .mlm-zoom-controls {
                        position: absolute;
                        bottom: 60px;
                        right: 12px;
                        z-index: 1000;
                    }

                    .mlm-instructions {
                        position: absolute;
                        top: 12px;
                        left: 12px;
                        z-index: 100;
                        backdrop-filter: blur(4px);
                    }

                    /* Search Panel Styles - Matching OrgChartTree */
                    .mlm-search-wrapper {
                        position: absolute;
                        top: 12px;
                        right: 12px;
                        z-index: 1000;
                    }

                    .mlm-search-btn {
                        width: 40px;
                        height: 40px;
                        font-size: 1rem;
                        box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
                    }

                    .mlm-search-panel {
                        width: calc(100vw - 24px);
                        max-width: 320px;
                        max-height: 60vh;
                        overflow-y: auto;
                    }

                    /* Tablet and Desktop styles */
                    @media (min-width: 768px) {
                        .mlm-search-wrapper {
                            top: 20px;
                            right: 20px;
                        }

                        .mlm-search-btn {
                            width: 48px;
                            height: 48px;
                            font-size: 1.2rem;
                        }

                        .mlm-search-panel {
                            width: 400px;
                            max-width: 400px;
                            max-height: 70vh;
                        }
                    }

                    /* Large Desktop styles */
                    @media (min-width: 992px) {
                        .mlm-zoom-controls {
                            bottom: 16px;
                            right: 16px;
                        }

                        .mlm-instructions {
                            top: 20px;
                            left: 20px;
                        }
                    }
                `}
            </style>
        </div>
    );
};

export default MLMTreeVisualization;
